/**
 * Firebase Coupons Service
 * Handles Firestore coupon operations.
 *
 * Convention: the Firestore document ID IS the uppercased coupon code, so
 * lookups by code are a single-doc `get` (not an enumerable `list`).
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db, COLLECTIONS } from '@/config/firebase.config';
import {
  Coupon,
  CouponFormData,
  CouponStatus,
  CouponValidationResult,
  computeCouponDiscount,
} from '@/types/coupon.types';

/**
 * Normalize a raw code into the canonical document ID / stored code
 */
export const normalizeCouponCode = (code: string): string =>
  code.trim().toUpperCase();

/**
 * Derive a display status from a coupon document
 */
export const getCouponStatus = (coupon: Coupon): CouponStatus => {
  if (!coupon.isActive) return CouponStatus.INACTIVE;
  if (coupon.expiresAt && coupon.expiresAt.toMillis() < Date.now()) {
    return CouponStatus.EXPIRED;
  }
  if (coupon.usageCount >= coupon.usageLimit) return CouponStatus.USED;
  return CouponStatus.ACTIVE;
};

/**
 * Get all coupons (admin listing), newest first
 */
export const getAllCoupons = async (): Promise<Coupon[]> => {
  try {
    const q = query(
      collection(db, COLLECTIONS.COUPONS),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Coupon));
  } catch (error) {
    console.error('Error fetching coupons:', error);
    throw new Error('Failed to fetch coupons');
  }
};

/**
 * Get a coupon by its code (== document ID). Single-doc get.
 */
export const getCouponByCode = async (code: string): Promise<Coupon | null> => {
  try {
    const id = normalizeCouponCode(code);
    if (!id) return null;
    const docSnap = await getDoc(doc(db, COLLECTIONS.COUPONS, id));
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() } as Coupon;
  } catch (error) {
    console.error('Error fetching coupon by code:', error);
    throw new Error('Failed to fetch coupon');
  }
};

/**
 * Get a coupon by its document ID (which is the code). Alias of getCouponByCode.
 */
export const getCouponById = (id: string): Promise<Coupon | null> =>
  getCouponByCode(id);

/**
 * Create a new coupon. Rejects if the code already exists.
 */
export const createCoupon = async (formData: CouponFormData): Promise<string> => {
  try {
    const id = normalizeCouponCode(formData.code);
    if (!id) {
      throw new Error('Coupon code is required');
    }

    const existing = await getDoc(doc(db, COLLECTIONS.COUPONS, id));
    if (existing.exists()) {
      throw new Error('A coupon with this code already exists');
    }

    const now = Timestamp.now();
    const couponData = {
      code: id,
      discountType: formData.discountType,
      discountValue: formData.discountValue,
      usageLimit: formData.usageLimit,
      usageCount: 0,
      isActive: formData.isActive,
      createdAt: now,
      updatedAt: now,
      ...(formData.description ? { description: formData.description } : {}),
      ...(formData.minOrderValue != null ? { minOrderValue: formData.minOrderValue } : {}),
      ...(formData.maxDiscount != null ? { maxDiscount: formData.maxDiscount } : {}),
      ...(formData.expiresAt ? { expiresAt: formData.expiresAt } : {}),
    };

    await setDoc(doc(db, COLLECTIONS.COUPONS, id), couponData);
    return id;
  } catch (error) {
    console.error('Error creating coupon:', error);
    throw error instanceof Error ? error : new Error('Failed to create coupon');
  }
};

/**
 * Update an existing coupon. The code (doc ID) is immutable.
 */
export const updateCoupon = async (
  id: string,
  formData: Partial<CouponFormData>
): Promise<void> => {
  try {
    const updates: { [key: string]: number | string | boolean | Timestamp | null } = {
      updatedAt: Timestamp.now(),
    };

    if (formData.discountType !== undefined) updates.discountType = formData.discountType;
    if (formData.discountValue !== undefined) updates.discountValue = formData.discountValue;
    if (formData.usageLimit !== undefined) updates.usageLimit = formData.usageLimit;
    if (formData.isActive !== undefined) updates.isActive = formData.isActive;
    if (formData.description !== undefined) updates.description = formData.description;
    if (formData.minOrderValue !== undefined) {
      updates.minOrderValue = formData.minOrderValue ?? null;
    }
    if (formData.maxDiscount !== undefined) {
      updates.maxDiscount = formData.maxDiscount ?? null;
    }
    if (formData.expiresAt !== undefined) {
      updates.expiresAt = formData.expiresAt ?? null;
    }

    await updateDoc(doc(db, COLLECTIONS.COUPONS, id), updates);
  } catch (error) {
    console.error('Error updating coupon:', error);
    throw new Error('Failed to update coupon');
  }
};

/**
 * Delete a coupon by ID (code)
 */
export const deleteCoupon = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.COUPONS, id));
  } catch (error) {
    console.error('Error deleting coupon:', error);
    throw new Error('Failed to delete coupon');
  }
};

/**
 * Validate a coupon code against a cart subtotal (read-only; does not consume).
 * Returns the computed discount and a user-facing message.
 */
export const validateCoupon = async (
  code: string,
  subtotal: number
): Promise<CouponValidationResult> => {
  const coupon = await getCouponByCode(code);

  if (!coupon) {
    return { valid: false, message: 'Invalid coupon code', discount: 0 };
  }
  if (!coupon.isActive) {
    return { valid: false, message: 'This coupon is no longer active', discount: 0 };
  }
  if (coupon.expiresAt && coupon.expiresAt.toMillis() < Date.now()) {
    return { valid: false, message: 'This coupon has expired', discount: 0 };
  }
  if (coupon.usageCount >= coupon.usageLimit) {
    return { valid: false, message: 'This coupon has already been used', discount: 0 };
  }
  if (coupon.minOrderValue != null && subtotal < coupon.minOrderValue) {
    return {
      valid: false,
      message: `Minimum order value for this coupon is ₹${coupon.minOrderValue}`,
      discount: 0,
    };
  }

  const discount = computeCouponDiscount(coupon, subtotal);
  if (discount <= 0) {
    return { valid: false, message: 'This coupon cannot be applied to your cart', discount: 0 };
  }

  return { valid: true, message: 'Coupon applied', discount, coupon };
};
